import { useEffect, useState, useRef } from "react";
import {
  X, Download, CheckCircle2, AlertCircle, ShieldCheck, Upload, Loader2, FileText,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DscSignModal — Adobe-style DSC signing flow (vendor-neutral).
 *
 * We DO NOT bridge the USB DSC token through the browser. Instead:
 *   1. Backend generates the resolution PDF with EMPTY signature widgets
 *      pre-placed for each signing director (one box per director, on the
 *      last page, with the director name + DIN captioned above).
 *   2. User downloads this PDF, opens it in Adobe Reader (free) on the
 *      same PC where their DSC USB token is plugged in.
 *   3. Each empty signature widget shows "Click to sign" in Adobe.
 *   4. Director clicks → Adobe shows ALL their DSCs (Windows Cert Store +
 *      PKCS#11 — eMudhra, Capricorn, (n)Code, Sify, Pantasign, anything).
 *   5. Director picks their DSC, enters PIN in Adobe's secure dialog,
 *      Adobe signs cryptographically and saves the PDF.
 *   6. (Optional) User uploads the signed PDF back to ROC Vault so we can
 *      verify + archive + show in audit trail.
 *
 * Zero browser extension. Zero vendor lock-in. Works with any DSC.
 */
export default function DscSignModal({ open, onClose, resolution, company, onSigned }) {
  const [step, setStep] = useState("ready");   // ready | uploading | done | error
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep("ready");
    setError("");
    setResult(null);
  }, [open]);

  if (!open) return null;

  // Resolve the signatories the backend will place fields for.
  const sigDirs = (resolution?.signing_directors && resolution.signing_directors.length > 0)
    ? resolution.signing_directors
    : (company?.directors || []).slice(0, 2);

  const downloadPdfWithFields = () => {
    window.open(`${API}/resolutions/${resolution.id}/dsc/pdf-with-fields`, "_blank");
    toast.success("Downloading PDF with empty signature fields");
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Please upload a PDF file");
      return;
    }
    setStep("uploading");
    setError("");
    try {
      const b64 = await fileToBase64(file);
      const r = await axios.post(
        `${API}/resolutions/${resolution.id}/dsc/upload-signed`,
        { signed_pdf_base64: b64 },
        { withCredentials: true },
      );
      setResult(r.data);
      setStep("done");
      onSigned?.(r.data);
      toast.success(`${r.data.signatures_stored} signature(s) verified & archived`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
      setStep("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/60 backdrop-blur-sm p-4"
      data-testid="dsc-modal-overlay"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-xl w-full shadow-2xl border border-zinc-200"
        data-testid="dsc-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#002FA7]" />
            <div>
              <div className="font-heading text-base font-medium">Sign with DSC</div>
              <div className="text-[11px] text-zinc-500 font-mono-plex">
                {resolution?.resolution_number}
              </div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="dsc-close"
            className="text-zinc-400 hover:text-zinc-700">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {step === "ready" && (
            <div className="space-y-5" data-testid="dsc-step-ready">
              <p className="text-sm text-zinc-700 leading-relaxed">
                We&apos;ll prepare your resolution PDF with empty signature fields placed
                for each signing director. Open the file in <strong>Adobe Reader</strong> and click
                each field to sign with <strong>any DSC</strong> — eMudhra, Capricorn, (n)Code,
                Sify, Pantasign — all work the same way through Adobe.
              </p>

              {sigDirs.length > 0 && (
                <div className="border border-zinc-200 bg-zinc-50 px-4 py-3 text-xs">
                  <div className="label-eyebrow mb-2">Signature fields will be placed for</div>
                  <div className="space-y-1.5">
                    {sigDirs.map((d, i) => (
                      <div key={i} className="flex items-center justify-between" data-testid={`dsc-signatory-${i}`}>
                        <div className="font-medium text-zinc-900">{d.name}</div>
                        <div className="text-zinc-500 font-mono-plex">
                          {d.din ? `DIN ${d.din}` : ""} {d.designation ? ` · ${d.designation}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={downloadPdfWithFields}
                className="swiss-btn-primary w-full text-sm"
                data-testid="dsc-download-pdf"
              >
                <Download size={14} /> Download PDF with signature fields
              </button>

              <details className="text-xs text-zinc-700 border border-zinc-200 px-3 py-2 bg-zinc-50">
                <summary className="cursor-pointer font-medium text-zinc-900 py-1">How to sign (60 seconds)</summary>
                <ol className="list-decimal pl-5 space-y-1.5 mt-2 leading-relaxed">
                  <li>Plug your DSC USB token into the PC.</li>
                  <li>Open the downloaded PDF in <strong>Adobe Reader DC</strong> (free).</li>
                  <li>Scroll to the last page — you&apos;ll see &ldquo;Click to sign&rdquo; boxes with each director&apos;s name.</li>
                  <li>Click your box → Adobe shows your DSCs → pick yours → enter PIN.</li>
                  <li>Save the file. Adobe shows green-tick on the signature.</li>
                  <li>(Optional) Upload the signed PDF below to archive in your audit trail.</li>
                </ol>
              </details>

              <div className="pt-4 border-t border-zinc-200">
                <div className="label-eyebrow mb-2">Step 2 (optional) — Upload signed PDF for audit trail</div>
                <input
                  type="file" accept=".pdf,application/pdf"
                  ref={fileInputRef}
                  onChange={onFileChosen}
                  className="hidden"
                  data-testid="dsc-file-input"
                />
                <button
                  onClick={onPickFile}
                  className="swiss-btn-ghost w-full text-sm"
                  data-testid="dsc-upload-signed-btn"
                >
                  <Upload size={14} /> Upload signed PDF
                </button>
              </div>
            </div>
          )}

          {step === "uploading" && (
            <div className="text-center py-10 space-y-3" data-testid="dsc-step-uploading">
              <Loader2 size={28} className="animate-spin mx-auto text-[#002FA7]" />
              <div className="text-sm text-zinc-700">Verifying signatures &amp; archiving…</div>
            </div>
          )}

          {step === "done" && result && (
            <div className="space-y-4" data-testid="dsc-step-done">
              <div className="flex items-start gap-3 border border-emerald-200 bg-emerald-50 px-4 py-3">
                <CheckCircle2 size={22} className="text-emerald-700 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium text-emerald-900">
                    {result.signatures_stored} signature{result.signatures_stored !== 1 ? "s" : ""} archived
                  </div>
                  <div className="text-[11px] text-emerald-800 mt-1 space-y-0.5">
                    {result.signers.map((s, i) => (
                      <div key={i} className="font-mono-plex">
                        {s.signer_common_name || "(no CN)"} · issued by {s.issuer_common_name || "—"}
                        {s.signing_time && ` · ${new Date(s.signing_time).toLocaleString()}`}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="swiss-btn-primary w-full text-sm" data-testid="dsc-done-close">
                Done
              </button>
            </div>
          )}

          {step === "error" && (
            <div className="space-y-4" data-testid="dsc-step-error">
              <div className="flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>{error || "Something went wrong"}</div>
              </div>
              <button onClick={() => setStep("ready")} className="swiss-btn-ghost w-full text-sm" data-testid="dsc-error-retry">
                <FileText size={14} /> Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const dataUrl = fr.result;
      // strip "data:application/pdf;base64,"
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
}
